import React, { useState, useEffect, useCallback } from 'react';
import { 
  getCompetencias, 
  getDashboardGeral, 
  getDashboardEquipes,
  getAnaliseIndividual, 
  getAnaliseIndividualRegioes, 
  getAnaliseIndividualEquipes,
  getRota,
  getRotaRegioes,
  getRotaEquipes,
} from './api/dashboard';
import Header from './components/Header';
import DropdownCompetencia from './components/DropdownCompetencia';
import AbaGeral from './components/AbaGeral';
import AbaEquipes from './components/AbaEquipes';
import AbaAnaliseIndividual from './components/AbaAnaliseIndividual';
import AbaRota from './components/AbaRota';

const EH_SITE_EQUIPES = window.location.hostname.startsWith('equipes');

/**
 * Error Boundary para capturar exceções no render de abas
 * (Leaflet/Mapa podem lançar erros que derrubam toda a aplicação)
 */
class AbaErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Erro em aba:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          color: 'var(--muted)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
        }}>
          <h3 style={{ color: 'var(--red)', marginBottom: '12px' }}>
            ⚠️ Ocorreu um erro ao exibir esta aba
          </h3>
          <p style={{ fontSize: 'var(--font-sm)' }}>
            {this.state.error?.message || 'Erro desconhecido'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              background: 'var(--amber)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            Tentar novamente
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [competencias, setCompetencias] = useState([]);
  const [competenciaAtual, setCompetenciaAtual] = useState(null);
  const [dadosGeral, setDadosGeral] = useState(null);
  const [dadosEquipes, setDadosEquipes] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState('Geral');

  // Título da aba do navegador
  useEffect(() => {
    document.title = EH_SITE_EQUIPES ? 'Equipes | Imobluz' : 'Dashboard Imobluz';
  }, []);

  // Carregar competências ao iniciar
  useEffect(() => {
    getCompetencias()
      .then(lista => {
        setCompetencias(lista);
        if (lista.length > 0) {
          setCompetenciaAtual(lista[0]);
        }
      })
      .catch(err => console.error('Erro ao carregar competências:', err));
  }, []);

  // Carregar dados quando competência muda
  const carregarDados = useCallback(async (comp) => {
    if (!comp) return;
    setCarregando(true);
    try {
      if (EH_SITE_EQUIPES) {
        const equipes = await getDashboardEquipes(comp.id);
        setDadosEquipes(equipes);
      } else {
        const [geral, equipes] = await Promise.all([
          getDashboardGeral(comp.id),
          getDashboardEquipes(comp.id),
        ]);
        setDadosGeral(geral);
        setDadosEquipes(equipes);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (competenciaAtual) {
      carregarDados(competenciaAtual);
    }
  }, [competenciaAtual, carregarDados]);

  const handleChangeCompetencia = (comp) => {
    setCompetenciaAtual(comp);
  };

  // === Site exclusivo de Equipes ===
  if (EH_SITE_EQUIPES) {
    return (
      <div className="dashboard" style={{ minHeight: '100vh' }}>
        <Header
          abaAtiva="Equipes"
          onChangeAba={() => {}}
          dropdown={
            <DropdownCompetencia
              valor={competenciaAtual}
              opcoes={competencias}
              onChange={handleChangeCompetencia}
            />
          }
        />
        <main style={{ padding: '24px', maxWidth: '1440px', margin: '0 auto', width: '100%' }}>
          <AbaEquipes competencia={competenciaAtual?.id} />
        </main>
      </div>
    );
  }

  // === Site completo (dashboard original) ===
  return (
    <div className="dashboard" style={{ minHeight: '100vh' }}>
      <Header
        abaAtiva={abaAtiva}
        onChangeAba={setAbaAtiva}
        dropdown={
          <DropdownCompetencia
            valor={competenciaAtual}
            opcoes={competencias}
            onChange={handleChangeCompetencia}
          />
        }
      />

      <main style={{ padding: '24px', maxWidth: '1440px', margin: '0 auto', width: '100%' }}>
        <AbaErrorBoundary>
          {abaAtiva === 'Geral' && (
            <AbaGeral
              geral={dadosGeral}
              carregando={carregando}
            />
          )}
        </AbaErrorBoundary>
        <AbaErrorBoundary>
          {abaAtiva === 'Equipes' && (
            <AbaEquipes competencia={competenciaAtual?.id} />
          )}
        </AbaErrorBoundary>
        <AbaErrorBoundary>
          {abaAtiva === 'Análise Individual' && (
            <AbaAnaliseIndividual 
              competencia={competenciaAtual?.id}
              competencias={competencias}
            />
          )}
        </AbaErrorBoundary>
        <AbaErrorBoundary>
          {abaAtiva === 'Rota' && (
            <AbaRota 
              competencia={competenciaAtual?.id}
              competencias={competencias}
              equipesLista={dadosEquipes}
            />
          )}
        </AbaErrorBoundary>
      </main>
    </div>
  );
}